import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate, formatMinor } from '../../lib/format';
import type { Invoice, InvoiceStatus } from '../../lib/types';
import { Badge, Card, DataTable, Modal, PageHeader, Spinner } from '../../components/ui';

const STATUS_TONE: Record<InvoiceStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  draft: 'neutral',
  open: 'info',
  paid: 'success',
  overdue: 'danger',
  void: 'neutral',
  uncollectible: 'danger',
};

/** Read-only per the brief ("invoices from Loctopus - read-only list/detail views"). */
export function InvoicesPage() {
  const [selected, setSelected] = useState<Invoice | null>(null);
  const invoicesQuery = useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.invoices.list({ limit: 100 }),
  });

  return (
    <div>
      <PageHeader title="Invoices" description="Loctopus platform fee invoices for this account." />
      {invoicesQuery.isLoading ? (
        <Spinner />
      ) : invoicesQuery.error ? (
        <ErrorSummary error={toParsedError(invoicesQuery.error)} />
      ) : (
        <DataTable<Invoice>
          columns={[
            { key: 'number', header: 'Invoice #', render: (i) => `#${i.invoiceNumber}` },
            { key: 'issued', header: 'Issued', render: (i) => formatDate(i.issueDate) },
            { key: 'due', header: 'Due', render: (i) => formatDate(i.dueDate) },
            {
              key: 'total',
              header: 'Total (incl. GST)',
              render: (i) => formatMinor(i.totalMinor, i.currency),
            },
            {
              key: 'status',
              header: 'Status',
              render: (i) => <Badge tone={STATUS_TONE[i.status]}>{i.status}</Badge>,
            },
          ]}
          rows={invoicesQuery.data?.data ?? []}
          getRowKey={(i) => i.id}
          onRowClick={setSelected}
          emptyMessage="No invoices yet."
        />
      )}
      <InvoiceDetailModal invoice={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function InvoiceDetailModal({
  invoice,
  onClose,
}: {
  invoice: Invoice | null;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['invoice', invoice?.id],
    queryFn: () => api.invoices.get(invoice!.id),
    enabled: !!invoice,
  });

  return (
    <Modal
      open={!!invoice}
      onClose={onClose}
      wide
      title={invoice ? `Invoice #${invoice.invoiceNumber}` : ''}
    >
      {detailQuery.isLoading ? (
        <Spinner />
      ) : detailQuery.data ? (
        <>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <Card title="Subtotal">
              {formatMinor(detailQuery.data.subtotalMinor, detailQuery.data.currency)}
            </Card>
            <Card title="GST">
              {formatMinor(detailQuery.data.gstMinor, detailQuery.data.currency)}
            </Card>
            <Card title="Total">
              {formatMinor(detailQuery.data.totalMinor, detailQuery.data.currency)}
            </Card>
          </div>
          <DataTable
            columns={[
              { key: 'desc', header: 'Description', render: (l) => l.description },
              { key: 'qty', header: 'Qty', render: (l) => l.quantity },
              {
                key: 'unit',
                header: 'Unit',
                render: (l) => formatMinor(l.unitAmountMinor, l.currency),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (l) => formatMinor(l.amountMinor, l.currency),
              },
            ]}
            rows={detailQuery.data.lines}
            getRowKey={(l) => l.id}
            emptyMessage="No line items."
          />
          {detailQuery.data.pdfR2Key && (
            <a
              className="inline-block mt-4 text-brand-accent underline"
              href={`/dashboard/invoices/${detailQuery.data.id}/pdf`}
            >
              Download PDF
            </a>
          )}
        </>
      ) : null}
    </Modal>
  );
}
